import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Divider, LinearProgress } from '@mui/material';

import {
    Container,
    ErrorMessage,
    SimpleSingleSelect,
    SimpleMultipleSelect,
    SimpleTextField,
    Loading,
    SimpleButton,
} from '@/src/ui';
import { getZeroOption, type Option } from '@/src/utils';
import { apiRequest } from '@/src/services';
import {
    fullStandardOptionsRaw,
    QUANTITY_OPTIONS,
    type FilterOptions,
    type QuestionTypeOption,
    type RequestParams,
    type QuestionAndIdDTO,
    type ConfigSet,
    type DynamicQuizFormValues,
    type QuestionGenerationDTO,
    type QuestionVariant,
    schema,
} from './dtos';
import { QuestionRenderer } from '@/src/lib';

const fullStandardOptions: Record<number, QuestionTypeOption[]> = {};

const NoStandardsFound = getZeroOption('No standards found for the selected grades');
const SelectStandardOption = getZeroOption('Select a standard');
const NoVariantsOption = getZeroOption('No variants available');

const defaultConfigSet: ConfigSet = {
    standardOptionId: 0,
    variantIds: [],
    quantity: 0,
};

const DynamicQuizGeneratorPage: React.FC = () => {
    document.title = 'Dynamic Quiz Generator';

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [questions, setQuestions] = useState<QuestionGenerationDTO[]>([]);

    const [gradeOptions, setGradeOptions] = useState<Option[]>([]);

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
    } = useForm<DynamicQuizFormValues>({
        defaultValues: {
            intendedGrades: [],
            title: '',
            standardGrades: [],
            config: [{ ...defaultConfigSet }],
        },
        resolver: yupResolver(schema as any),
        mode: 'onBlur',
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'config',
    });

    const standardGrades = watch('standardGrades');
    const config = watch('config');

    // Initialize options and translate standard names to IDs
    useEffect(() => {
        (async function init() {
            try {
                const res = await apiRequest<FilterOptions>(
                    'GET',
                    `utility/filterData?getGradeLevelMaster=true&getStandard=true`
                );
                const newGradeOptions = res.data.gradeLevelMasterOptions;
                const standardOptions = res.data.standardOptions;
                setGradeOptions(newGradeOptions);

                // Translate Standard Names to DB IDs
                Object.entries(fullStandardOptionsRaw).forEach(([gradeId, options]) => {
                    const fsoArray: QuestionTypeOption[] = [];
                    options.forEach((option: QuestionTypeOption) => {
                        option.standardIds = [];
                        option.standards.forEach((standard: string) => {
                            const matchingStandard = standardOptions.find(
                                (s) => s.name === standard
                            );
                            if (matchingStandard) {
                                option.standardIds!.push(Number(matchingStandard.id));
                            }
                        });
                        fsoArray.push(option);
                    });
                    fullStandardOptions[Number(gradeId)] = fsoArray;
                });
            } catch {
                setError('Failed to fetch data');
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const availableStandards = useMemo(() => {
        const result: QuestionTypeOption[] = [];
        Object.entries(fullStandardOptions).forEach(([gradeId, options]) => {
            if (!standardGrades.includes(Number(gradeId))) return;
            options.forEach(
                (option: QuestionTypeOption) => (option.name = option.standards.join(', '))
            );
            result.push(...options);
        });
        return result.sort((a, b) => a.name!.localeCompare(b.name!));
    }, [standardGrades]);

    const availableStandardOptions = useMemo(() => {
        const options: Option[] = availableStandards.map((standard) => ({
            id: standard.id,
            name: standard.name || `Unknown Standard ${standard.id}`,
        }));
        if (!availableStandards.length) {
            options.push(NoStandardsFound);
        } else {
            options.unshift(SelectStandardOption);
        }
        return options;
    }, [availableStandards]);

    const getVariantOptions = useCallback(
        (standardOptionId: number) => {
            const standardOption = availableStandards.find(
                (option) => option.id === standardOptionId
            );
            if (!standardOption) return [];
            const options: Option[] = standardOption.variants.map((variant) => ({
                id: variant.id,
                name: variant.name,
            }));
            if (!options.length) options.push(NoVariantsOption);
            return options;
        },
        [availableStandards]
    );

    const handleAddConfigSet = useCallback(() => {
        if (fields.length >= 5) return;
        append({ ...defaultConfigSet });
    }, [fields.length, append]);

    const [nQuestionsLoaded, setNQuestionsLoaded] = useState(0);
    const [nQuestionsTotal, setNQuestionsTotal] = useState(0);
    const generateAllQuestions = useCallback(
        async (data: DynamicQuizFormValues) => {
            const requestData: RequestParams[] = [];
            data.config.forEach((configItem) => {
                const variantParams = configItem.variantIds
                    .map((variantId) => {
                        const standardOption = availableStandards.find(
                            (option) => option.id === configItem.standardOptionId
                        );
                        if (!standardOption) return null;

                        const variant = standardOption.variants.find((v) => v.id === variantId);
                        if (!variant) return null;

                        return variant;
                    })
                    .filter((params) => params !== null) as QuestionVariant[];

                // Save standardIds for submission to server late on
                const standards =
                    availableStandards.find((option) => option.id === configItem.standardOptionId)
                        ?.standardIds || [];
                for (let i = 0; i < configItem.quantity + 1; i++) {
                    requestData.push({
                        variants: variantParams,
                        standards,
                    });
                }
            });

            if (requestData.length > 50) {
                setError('Cannot generate more than 50 questions at a time');
                return;
            }

            setError('');
            setLoading(true);
            setQuestions([]);
            setNQuestionsLoaded(0);
            setNQuestionsTotal(requestData.length);

            for (const request of requestData) {
                const variant =
                    request.variants[Math.floor(Math.random() * request.variants.length)];
                try {
                    const res = await apiRequest<QuestionAndIdDTO>(
                        'POST',
                        `/assessments/questions/dynamic/${variant.route}`,
                        variant.params
                    );
                    const newQuestion = res.data.question;
                    const newQuestionDTO: QuestionGenerationDTO = {
                        ...newQuestion,
                        variantsForRegeneration: request.variants,
                        standards: request.standards,
                    };
                    setQuestions((prev) => [...prev, newQuestionDTO]);
                    setNQuestionsLoaded((prev) => prev + 1);
                } catch (e) {
                    console.error(e);
                    setError('Failed to fetch questions');
                    break;
                }
            }
            setLoading(false);
        },
        [availableStandards]
    );
    const handleGenerateClick = useCallback(() => {
        if (questions.length > 0) {
            const confirmed = window.confirm('This will replace your current questions. Continue?');
            if (!confirmed) return;
        }
        handleSubmit(generateAllQuestions)();
    }, [questions.length, handleSubmit, generateAllQuestions]);

    const [loadingSingleIndex, setLoadingSingleIndex] = useState<number | null>(null);
    const regenerateSingleQuestion = useCallback(
        async (index: number, question: QuestionGenerationDTO) => {
            setLoadingSingleIndex(index);
            const variant =
                question.variantsForRegeneration[
                    Math.floor(Math.random() * question.variantsForRegeneration.length)
                ];
            try {
                const res = await apiRequest<QuestionAndIdDTO>(
                    'POST',
                    `/assessments/questions/dynamic/${variant.route}`,
                    variant.params
                );
                const newQuestion = res.data.question;
                const newQuestionDTO: QuestionGenerationDTO = {
                    ...newQuestion,
                    variantsForRegeneration: question.variantsForRegeneration,
                    standards: question.standards,
                };
                setQuestions((prev) => {
                    const newQuestions = [...prev];
                    newQuestions[index] = newQuestionDTO;
                    return newQuestions;
                });
                setNQuestionsLoaded(1);
            } catch {
                setError('Failed to regenerate question');
            } finally {
                setLoadingSingleIndex(null);
            }
        },
        []
    );

    return (
        <Container>
            <h1 className="page-header">Dynamic Quiz Generator</h1>
            <br />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                    gap: 2,
                    flexWrap: 'wrap',
                }}
            >
                <SimpleMultipleSelect
                    sx={{ flex: 1 }}
                    name="intendedGrades"
                    label="Intended Grades"
                    options={gradeOptions}
                    value={watch('intendedGrades')}
                    onChange={(val) => setValue('intendedGrades', val)}
                />
                <SimpleTextField
                    sx={{ flex: 1 }}
                    name="title"
                    label="Quiz Title"
                    value={watch('title')}
                    onChange={(e) => setValue('title', e.target.value)}
                />
                <SimpleMultipleSelect
                    sx={{ flex: 1 }}
                    name="standardGrades"
                    label="Standard Grades"
                    options={gradeOptions}
                    value={watch('standardGrades')}
                    onChange={(val) => setValue('standardGrades', val)}
                />
            </Box>
            <ErrorMessage error={errors.standardGrades?.message} />
            <br />
            {fields.map((field, index) => {
                if (!standardGrades.length) return null;
                return (
                    <Box key={field.id} sx={{ mb: 2 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: 2,
                                flexWrap: 'wrap',
                            }}
                        >
                            <SimpleSingleSelect
                                sx={{
                                    flex: 1,
                                    width: '100%',
                                    '& .MuiSelect-select': { width: '100%' },
                                }}
                                name={`config.${index}.standardOptionId`}
                                label="Standard"
                                options={availableStandardOptions}
                                value={config[index]?.standardOptionId ?? 0}
                                onChange={(e) =>
                                    setValue(
                                        `config.${index}.standardOptionId`,
                                        Number(e.target.value)
                                    )
                                }
                            />
                            <SimpleMultipleSelect
                                sx={{ flex: 1 }}
                                name={`config.${index}.variantIds`}
                                label="Variant"
                                options={getVariantOptions(config[index]?.standardOptionId ?? 0)}
                                value={config[index]?.variantIds ?? []}
                                onChange={(val) => setValue(`config.${index}.variantIds`, val)}
                            />
                            <SimpleSingleSelect
                                sx={{ flex: 1 }}
                                name={`config.${index}.quantity`}
                                label="Quantity"
                                options={QUANTITY_OPTIONS}
                                value={config[index]?.quantity ?? 0}
                                onChange={(e) =>
                                    setValue(`config.${index}.quantity`, Number(e.target.value))
                                }
                            />
                            {index !== 0 && (
                                <SimpleButton
                                    variant="outlined"
                                    color="error"
                                    onClick={() => remove(index)}
                                >
                                    Remove
                                </SimpleButton>
                            )}
                        </Box>
                        <ErrorMessage error={errors.config?.[index]?.standardOptionId?.message} />
                        <ErrorMessage error={errors.config?.[index]?.variantIds?.message} />
                    </Box>
                );
            })}
            {standardGrades.length > 0 && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                    }}
                >
                    <div>
                        {fields.length < 5 && (
                            <SimpleButton
                                variant="contained"
                                onClick={handleAddConfigSet}
                                disabled={fields.length >= 5}
                            >
                                Add Standard
                            </SimpleButton>
                        )}
                    </div>
                    {fields.length > 0 && (
                        <SimpleButton variant="contained" onClick={handleGenerateClick}>
                            Generate Quiz
                        </SimpleButton>
                    )}
                </Box>
            )}
            {loading && (
                <>
                    <div className="mb-2">
                        <LinearProgress
                            variant="determinate"
                            value={(nQuestionsLoaded * 100) / (nQuestionsTotal || 100)}
                        />
                    </div>
                    Loading questions ({nQuestionsLoaded} / {nQuestionsTotal})...
                </>
            )}

            <Divider sx={{ my: 2 }} />

            {!loading &&
                questions.map((question, index) => {
                    if (loadingSingleIndex === index) {
                        return <Loading key={`question-box-${index}`} />;
                    }
                    return (
                        <Box key={`question-box-${index}`} sx={{ mb: 4 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}
                            >
                                <span>Question {index + 1}</span>
                                <SimpleButton
                                    onClick={() => regenerateSingleQuestion(index, question)}
                                >
                                    Regenerate
                                </SimpleButton>
                            </Box>
                            <QuestionRenderer
                                key={`question-${index}`}
                                question={question}
                                stimMode="preview"
                                options={{ showScoring: true }}
                            />
                        </Box>
                    );
                })}
            <ErrorMessage error={error} />
            <ErrorMessage error={errors.config?.root?.message} />
        </Container>
    );
};

export default DynamicQuizGeneratorPage;
